import React, { useEffect, useState } from "react";
import "./Landing.css";
import googlePlay from "../../assets/playstore.png";
import appStore from "../../assets/appstore.png";
import PublicHeader from "../../components/public/PublicHeader";
import PublicFooter from "../../components/public/PublicFooter";

function Home() {
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const [showWaitlistThanks, setShowWaitlistThanks] = useState(false);

  useEffect(() => {
    if (!isWaitlistOpen) return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsWaitlistOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isWaitlistOpen]);

  const handleWaitlistSubmitted = () => {
    setShowWaitlistThanks(true);
    setTimeout(() => {
      setShowWaitlistThanks(false);
      setIsWaitlistOpen(false);
    }, 2000);
  };

  return (
    <div className="landing">
      <PublicHeader />
      <section className="public-hero">
        <div className="landing-container">
          <div className="landing-hero-content">
            <h1>Lifestyle intelligence for better everyday living.</h1>
            <p className="landing-lead">
              AI that understands how you live — across happiness, energy, time,
              habits, and life alignment — and helps you improve life outcomes
              over time.
            </p>
            <div className="landing-cta">
              <a href="#free-app" className="landing-btn primary">
                Download the Free App
              </a>
              <a href="#how-zinea-works" className="landing-btn secondary">
                How Zinea Works
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-section" id="problem">
        <div className="landing-container">
          <h2>Life isn’t lived in silos. Most apps treat it that way.</h2>
          <p>
            Mood, habits, productivity, and wellness don’t exist separately.
            When systems don’t understand this, people feel overwhelmed, burnt
            out, or stuck — without knowing why.
          </p>
          <p>Zinea was built to understand life as a system.</p>
        </div>
      </section>

      <section className="landing-section" id="how-zinea-works">
        <div className="landing-container">
          <h2>Zinea builds lifestyle intelligence.</h2>
          <p>
            Using AI, Zinea models how your life is going and how it changes over
            time. Instead of generic advice, Zinea adapts digital systems to
            reduce friction, improve balance, and support sustainable living.
          </p>
          <p className="landing-note">Less effort. Better design.</p>
        </div>
      </section>

      <section className="landing-section" id="core-dimensions">
        <div className="landing-container">
          <h2>Core dimensions</h2>
          <div className="landing-grid">
            <div className="landing-card">
              <h3>Emotional well-being</h3>
              <p>Happiness, stress, personal–professional balance</p>
            </div>
            <div className="landing-card">
              <h3>Energy &amp; vitality</h3>
              <p>Fatigue, recovery, sustainability</p>
            </div>
            <div className="landing-card">
              <h3>Time &amp; attention</h3>
              <p>Focus, overload, fragmentation</p>
            </div>
            <div className="landing-card">
              <h3>Habits &amp; stability</h3>
              <p>Routine consistency and friction</p>
            </div>
            <div className="landing-card">
              <h3>Life alignment</h3>
              <p>Direction, momentum, progress</p>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-section" id="free-app">
        <div className="landing-container">
          <h2>Start free. Build awareness.</h2>
          <p>Track how your life feels and changes over time.</p>
          <div className="landing-card">
            <h3>Included in Free App</h3>
            <ul className="landing-list">
              <li>Daily mood &amp; energy check-ins</li>
              <li>Personal and professional signals</li>
              <li>Basic routine tracking</li>
              <li>Simple trends &amp; summaries</li>
              <li>Utilities to support lifestyle </li>
            </ul>
          </div>
          <p className="landing-note">See what’s happening — not yet why.</p>
          <div className="landing-cta">
            <span className="landing-inline-note">Download links coming soon.</span>
          </div>

          {/*
          <div className="landing-store-links" id="download">
            <a
              href="https://play.google.com/store/apps/details?id=com.nipunai.zinea"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={googlePlay} alt="Get it on Google Play" />
            </a>
            <a
              href="https://apps.apple.com/app/id6504409326"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={appStore} alt="Download on the App Store" />
            </a>
          </div>
          */}

        </div>
      </section>

      <section className="landing-section" id="paid-intelligence">
        <div className="landing-container">
          <h2>Upgrade to lifestyle intelligence.</h2>
          <p>AI that explains patterns, detects early risk, and adapts your lifestyle systems.</p>
          <div className="landing-card">
            <h3>Unlock with Zinea Intelligence</h3>
            <ul className="landing-list">
              <li>Composite Happiness Index</li>
              <li>Energy &amp; burnout risk modeling</li>
              <li>Time &amp; attention insights</li>
              <li>Adaptive routines</li>
              <li>Lifestyle experiments that learn what works for you</li>
            </ul>
          </div>
          <p className="landing-note">Not more data. A system that adapts.</p>
          <div className="landing-cta">
            <button
              type="button"
              className="landing-btn primary"
              onClick={() => setIsWaitlistOpen(true)}
            >
              Unlock Zinea Intelligence
            </button>
          </div>
        </div>
      </section>

      <section className="landing-section" id="digital-physical">
        <div className="landing-container">
          <h2>Insight is digital. Life is physical.</h2>
          <p>
            Zinea designs optional physical tools to support focus, recovery, and
            consistency — translating intelligence into real-world action.
          </p>
          <p className="landing-note">
            Products support intelligence. Intelligence never exists to sell products.
          </p>
        </div>
      </section>

      <section className="landing-section" id="social-impact">
        <div className="landing-container">
          <h2>Impact by design.</h2>
          <p>
            By detecting instability early and reducing overload, Zinea helps
            prevent burnout and long-term disengagement — quietly, at scale.
          </p>
          <p>Better-designed lives create real impact.</p>
        </div>
      </section>

      <section className="landing-section landing-final">
        <div className="landing-container">
          <h2>Begin with awareness. Grow into intelligence.</h2>
          <div className="landing-cta">
            <a href="#free-app" className="landing-btn primary">
              Download the Free App
            </a>
            <a href="#paid-intelligence" className="landing-btn secondary">
              Explore Zinea Intelligence
            </a>
          </div>
        </div>
      </section>

      <section className="landing-footer-line">
        <div className="landing-container">
          <p>AI-powered lifestyle intelligence for sustainable living.</p>
        </div>
      </section>

      {isWaitlistOpen && (
        <div
          className="landing-modal-overlay"
          onClick={() => setIsWaitlistOpen(false)}
        >
          <div
            className="landing-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="landing-modal-header">
              <h3>Join the Waitlist</h3>
              <button
                type="button"
                className="landing-modal-close"
                onClick={() => setIsWaitlistOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="landing-modal-body">
              <iframe
                title="Zinea Life Waitlist"
                src="https://forms.zohopublic.in/connectzi1/form/ZineaLifeWaitlist/formperma/GEOGXeJvfe7noEzWZ8Lytu2Z6ZCWQOPyad-mVl4T_wQ"
                className="landing-modal-iframe"
              />
            </div>
            <div className="landing-modal-footer">
              {showWaitlistThanks ? (
                <span className="landing-modal-message">
                  Thanks for joining the waitlist, we will connect soon.
                </span>
              ) : (
                <button
                  type="button"
                  className="landing-btn primary"
                  onClick={handleWaitlistSubmitted}
                >
                  I have submitted
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <PublicFooter />
    </div>
  );
}

export default Home;
